/*
 * Delivery notification modal.
 *
 * Opens once per browser session on load, so returning shoppers are not
 * interrupted on every page view. In the theme editor it never auto-opens;
 * it opens when the editor selects the section, so the client can see what
 * they are editing.
 */
if (!customElements.get('delivery-notification')) {
  const STORAGE_KEY = 'pistabarfi-delivery-notification-dismissed';

  // Storage throws outright in some privacy modes, so every access is guarded
  // and a failure simply means the modal shows again next time.
  const storage = {
    isDismissed() {
      try {
        return sessionStorage.getItem(STORAGE_KEY) === 'true';
      } catch (error) {
        return false;
      }
    },
    setDismissed() {
      try {
        sessionStorage.setItem(STORAGE_KEY, 'true');
      } catch (error) {
        // Nothing to do: dismissal simply will not persist.
      }
    },
  };

  customElements.define(
    'delivery-notification',
    class DeliveryNotification extends HTMLElement {
      constructor() {
        super();
        this.dialog = this.querySelector('.delivery-notification__dialog');
        this.overlay = this.querySelector('[data-delivery-notification-overlay]');
        this.closeButton = this.querySelector('[data-delivery-notification-close]');
        this.activeElementBeforeOpen = null;
        this.lockedScroll = false;

        this.onCloseClick = this.close.bind(this);
        this.onOverlayClick = this.handleOverlayClick.bind(this);
        this.onKeyDown = this.handleKeyDown.bind(this);
      }

      connectedCallback() {
        if (!this.dialog || !this.overlay || !this.closeButton) return;

        this.closeButton.addEventListener('click', this.onCloseClick);
        this.overlay.addEventListener('click', this.onOverlayClick);
        this.addEventListener('keydown', this.onKeyDown);

        // Revealed only once wired up, so the markup cannot flash unstyled
        // for anyone whose JavaScript fails to load.
        this.setAttribute('data-initialized', '');

        if (this.isDesignMode) return;
        if (this.showOncePerSession && storage.isDismissed()) return;

        this.open();
      }

      disconnectedCallback() {
        this.closeButton?.removeEventListener('click', this.onCloseClick);
        this.overlay?.removeEventListener('click', this.onOverlayClick);
        this.removeEventListener('keydown', this.onKeyDown);

        // A section re-render mid-open must not strand the page unscrollable.
        this.releaseScrollLock();
      }

      get isDesignMode() {
        return this.dataset.designMode === 'true';
      }

      get showOncePerSession() {
        return this.dataset.showOncePerSession === 'true';
      }

      get isOpen() {
        return this.hasAttribute('open');
      }

      open() {
        if (this.isOpen) return;

        this.activeElementBeforeOpen = document.activeElement;
        this.setAttribute('open', '');

        // Only remove a lock this component added, so an open cart drawer or
        // another modal keeps its own.
        if (!document.body.classList.contains('overflow-hidden')) {
          document.body.classList.add('overflow-hidden');
          this.lockedScroll = true;
        }

        if (typeof trapFocus === 'function') {
          trapFocus(this.dialog, this.closeButton);
        } else {
          this.closeButton.focus();
        }
      }

      close() {
        if (!this.isOpen) return;

        this.removeAttribute('open');
        this.releaseScrollLock();

        if (this.showOncePerSession && !this.isDesignMode) {
          storage.setDismissed();
        }

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.activeElementBeforeOpen);
        } else if (this.activeElementBeforeOpen instanceof HTMLElement) {
          this.activeElementBeforeOpen.focus();
        }

        this.activeElementBeforeOpen = null;
      }

      releaseScrollLock() {
        if (!this.lockedScroll) return;
        document.body.classList.remove('overflow-hidden');
        this.lockedScroll = false;
      }

      handleOverlayClick(event) {
        // Only a click on the backdrop itself, never one bubbling out of the card.
        if (event.target === this.overlay) this.close();
      }

      handleKeyDown(event) {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.close();
        }
      }
    }
  );

  // Theme editor: show the modal while the section is selected so its content
  // and colours can be edited, and put it away again on deselect.
  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelector('delivery-notification')?.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    event.target.querySelector('delivery-notification')?.close();
  });
}
